/**
 * Rive layer for the Car Racing benchmark port.
 *
 * Single artboard ("Main" / state-machine "MainStageMachine") on a single
 * full-screen canvas. Mirrors
 *   packages/amgcarracing_v2/lib/presentation/rive/rive_controller_manager.dart
 *
 * Inputs are cached by name AND queried by `StateMachineInputType` (every
 * input in this runtime exposes both `value` and `fire()` — `fire()` is a
 * no-op on Number/Boolean — so type-checking via `.type` is the only safe
 * way to classify them).
 *
 * Nested inputs (e.g. history `result` under `Historyicon{N}`) go through
 * `setNumberStateAtPath`.
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import {
  BetPanelState,
  HistoryInputs,
  Main,
  MainInputs,
  TextRuns,
  WinLose,
  WinLoseInputs,
} from './riveInputs';

// ----------------------------------------------------------------------------
// Cached-input helpers
// ----------------------------------------------------------------------------

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

/**
 * Format a payout-odds value as the Rive label expects: "x" + the number with
 * any trailing ".00" stripped. 2.00 -> "x2", 40.00 -> "x40", 2.5 -> "x2.5".
 */
function formatOdd(value: number): string {
  if (Number.isInteger(value)) return `x${value}`;
  const s = value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return `x${s}`;
}

// ----------------------------------------------------------------------------
// Public types
// ----------------------------------------------------------------------------

export interface RiveLayerOptions {
  canvas: HTMLCanvasElement;
  /**
   * Optional second canvas for the MainWinLose.riv overlay (StartBet /
   * NoMoreBet / YOU WIN / YOU LOSE popups). When omitted, the winlose API
   * methods (fireStartBet/fireNoMoreBet/showWinLose) are no-ops.
   */
  winloseCanvas?: HTMLCanvasElement;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  // Forwarded Rive events — see _handleRiveEvent in
  // rive_controller_manager.dart:265-288.
  | 'BetButton'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'evtSoundOnOff'
  | 'evtMusicOnOff'
  | 'spinEnd'
  | 'allControllersReady'
  // Emitted by the MainWinLose.riv overlay when its popups play / finish.
  | 'StartBet'
  | 'NoMoreBet'
  | 'evtYouWinEnd'
  | 'evtYouLoseEnd';

type RiveEventCallback = (data: Record<string, unknown>) => void;

// ----------------------------------------------------------------------------
// Implementation
// ----------------------------------------------------------------------------

export class RiveLayer {
  private readonly canvas: HTMLCanvasElement;
  private readonly winloseCanvas: HTMLCanvasElement | null;
  private readonly assetBase: string;
  private instance: Rive | null = null;
  private inputs: Map<string, AnyInput> = new Map();
  // Second Rive instance for the MainWinLose.riv overlay (bet-window + result
  // popups). Lives on its own canvas/inputs map; null when no winlose canvas
  // was supplied. carracer.riv has none of these popups, so they're driven
  // from this separate full-screen overlay.
  private winlose: Rive | null = null;
  private winloseInputs: Map<string, AnyInput> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();

  constructor(opts: RiveLayerOptions) {
    this.canvas = opts.canvas;
    this.winloseCanvas = opts.winloseCanvas ?? null;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitCanvas();
    window.addEventListener('resize', () => this.fitCanvas());

    await Promise.all([this.load(), this.loadWinLose()]);
    this.dumpInputs();
    this.emit('ready', {});
  }

  private fitCanvas(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.fitOne(this.canvas, this.instance, dpr);
    this.fitOne(this.winloseCanvas, this.winlose, dpr);
  }

  private fitOne(
    canvas: HTMLCanvasElement | null,
    instance: Rive | null,
    dpr: number,
  ): void {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    if (instance) {
      try {
        instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private load(): Promise<void> {
    return new Promise((resolve, reject) => {
      const src = `${this.assetBase}${Main.file}`;
      const rive = new Rive({
        canvas: this.canvas,
        src,
        artboard: Main.artboard,
        stateMachines: Main.stateMachine,
        autoplay: true,
        layout: new Layout({ fit: Fit.Cover, alignment: Alignment.Center }),
        onLoad: () => {
          try {
            this.instance = rive;
            this.inputs = this.cacheInputs(rive, Main.stateMachine);
            this.attachEventBridge(rive);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(
            new Error(
              `Failed to load ${src} [${Main.artboard}/${Main.stateMachine}]: ${String(err)}`,
            ),
          );
        },
      });
    });
  }

  /**
   * Load the MainWinLose.riv overlay onto its own canvas. Resolves immediately
   * (no-op) when no winlose canvas was supplied. The "Button" artboard is a
   * full-screen transparent popup overlay — its events flow through the same
   * pub/sub bridge as the main artboard.
   */
  private loadWinLose(): Promise<void> {
    return new Promise((resolve, reject) => {
      const canvas = this.winloseCanvas;
      if (!canvas) {
        resolve();
        return;
      }
      const src = `${this.assetBase}${WinLose.file}`;
      const rive = new Rive({
        canvas,
        src,
        artboard: WinLose.artboard,
        stateMachines: WinLose.stateMachine,
        autoplay: true,
        // Contain keeps the centered popup graphic uncropped — the same way
        // amg_baccarat_pixi / amg_dragontiger_pixi load this overlay.
        layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
        onLoad: () => {
          try {
            this.winlose = rive;
            this.winloseInputs = this.cacheInputs(rive, WinLose.stateMachine);
            this.attachEventBridge(rive);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(
            new Error(
              `Failed to load ${src} [${WinLose.artboard}/${WinLose.stateMachine}]: ${String(err)}`,
            ),
          );
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sm: string): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    let list: unknown[] = [];
    try {
      list = rive.stateMachineInputs(sm) ?? [];
    } catch (e) {
      warn('[Rive] stateMachineInputs failed for', sm, e);
      return out;
    }
    for (const raw of list) {
      const inp = raw as {
        name: string;
        type: number;
        value: number | boolean;
        fire(): void;
      };
      switch (inp.type) {
        case StateMachineInputType.Number:
          out.set(inp.name, {
            type: 'number',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Number(inp.value ?? 0),
          });
          break;
        case StateMachineInputType.Boolean:
          out.set(inp.name, {
            type: 'boolean',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Boolean(inp.value),
          });
          break;
        case StateMachineInputType.Trigger:
          out.set(inp.name, {
            type: 'trigger',
            name: inp.name,
            fire: () => inp.fire(),
          });
          break;
        default:
          debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
      }
    }
    return out;
  }

  private dumpInputs(): void {
    this.dumpOne('main', Main.file, Main.stateMachine, this.inputs);
    if (this.winlose) {
      this.dumpOne('winlose', WinLose.file, WinLose.stateMachine, this.winloseInputs);
    }
  }

  private dumpOne(
    key: string,
    file: string,
    sm: string,
    inputs: Map<string, AnyInput>,
  ): void {
    const rows = Array.from(inputs.values()).map((i) => ({
      file,
      sm,
      name: i.name,
      type: i.type,
    }));
    // eslint-disable-next-line no-console
    console.groupCollapsed(`[Rive] ${key} (${file} / ${sm}) — ${rows.length} input(s)`);
    // eslint-disable-next-line no-console
    console.table(rows);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  private attachEventBridge(rive: Rive): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as {
          data?: { name?: string; properties?: Record<string, unknown> };
        };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}) };
        // Log EVERY event with its properties (not just unhandled ones) — this
        // is the ground-truth way to discover the .riv's event contract (event
        // names + custom props like `betID`). Enable with ?debug=1 or in dev.
        debugLog('[Rive][event]', name, props);
        if (this.listeners.has(name)) this.emit(name, props);
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // -----------------------------------------------------------------
  // Public high-level API — mirrors CarRacingRiveControllerManager
  // -----------------------------------------------------------------

  /** Race-position spin value. 0 = reset / hidden; 1..32 = final lane. */
  setSpin(value: number): void {
    this.setNumberAny(MainInputs.spin, value);
  }

  /**
   * Winning highlight lane (1..8). Caller must compute `378 - betItemId`.
   * Set `isEndHide = false` first so the highlight panel is visible.
   * Mirrors `_doEndingAnimation` in rive_controller_manager.dart:99-107.
   */
  setWinning(lane: number): void {
    this.setBoolAny(MainInputs.isEndHide, false);
    this.setNumberAny(MainInputs.winning, lane);
  }

  /** Clear the winning highlight (hides ending panel). */
  clearWinning(): void {
    this.setNumberAny(MainInputs.winning, 0);
  }

  /** Multiplayer round kickoff. Mirrors game_screen.dart:951-952. */
  fireDiceBowl(): void {
    this.setNumberAny(MainInputs.diceBowlPosition, 1.0);
    this.fireTriggerAny(MainInputs.trigDiceBowlPlay);
  }

  // -----------------------------------------------------------------
  // MainWinLose.riv overlay — StartBet / NoMoreBet / win-lose popups
  // -----------------------------------------------------------------

  /**
   * Fire the "place your bets" (StartBet) popup on the MainWinLose overlay:
   * set BetPanelNumber = 1, then fire BetPanelTrigger. The artboard emits a
   * `StartBet` Rive event when it plays. No-op when no winlose canvas loaded.
   */
  fireStartBet(): void {
    this.setNumberWinlose(WinLoseInputs.betPanelNumber, BetPanelState.startBet);
    this.fireTriggerWinlose(WinLoseInputs.betPanelTrigger);
  }

  /**
   * Fire the "no more bets" (NoMoreBet) popup on the MainWinLose overlay:
   * set BetPanelNumber = 2, then fire BetPanelTrigger. Emits a `NoMoreBet`
   * Rive event.
   */
  fireNoMoreBet(): void {
    this.setNumberWinlose(WinLoseInputs.betPanelNumber, BetPanelState.noMoreBet);
    this.fireTriggerWinlose(WinLoseInputs.betPanelTrigger);
  }

  /**
   * Show the YOU WIN / YOU LOSE popup on the MainWinLose overlay: set the
   * txtWonAmount text run, set YouWinLose (1 = win, 2 = lose), then fire
   * YouWinLoseTrigger. Emits `evtYouWinEnd` / `evtYouLoseEnd` when finished.
   */
  showWinLose(isWin: boolean, formattedAmount: string): void {
    if (this.winlose) {
      try {
        this.winlose.setTextRunValue(WinLoseInputs.txtWonAmount, formattedAmount);
      } catch {
        /* txtWonAmount not a text run on this build — skip */
      }
    }
    this.setNumberWinlose(WinLoseInputs.youWinLose, isWin ? 1 : 2);
    this.fireTriggerWinlose(WinLoseInputs.youWinLoseTrigger);
  }

  // NOTE: there is no `setSoundOn` — the `isSoundOn` input was removed from
  // MainStageMachine. The artboard emits its sound-cue events unconditionally
  // and audibility is gated by SoundManager (see audio/soundWiring.ts).

  setMobile(on: boolean): void {
    this.setBoolAny(MainInputs.isMobile, on);
  }

  // -----------------------------------------------------------------
  // History — nested inputs at Historyicon{N}/result
  // -----------------------------------------------------------------

  /**
   * Set a single history slot's value. `value` is the raw betItemId
   * (370..377); 0 = empty slot. Mirrors `_applyHistoryToRive` in
   * game_screen.dart:519-541 — tries multiple name/path candidates
   * silently because `.riv` exports move these around.
   */
  setHistory(index: number, value: number): void {
    if (index < 1 || index > 10) return;
    const paths = HistoryInputs.pathCandidates(index);
    const names = HistoryInputs.resultInputCandidates;
    for (const p of paths) {
      for (const n of names) {
        if (this.trySetNumberAtPathSilent(n, value, p)) return;
      }
    }
    if (index === 1) {
      debugLog('[Rive] setHistory: no compatible input found in carracer.riv');
    }
  }

  /**
   * Replace all 10 history slots from a list of betItemIds (newest first).
   * Pads empties with 0.
   */
  setAllHistory(results: ReadonlyArray<number>): void {
    for (let i = 0; i < 10; i++) {
      const v = i < results.length ? results[i] : 0;
      this.setHistory(i + 1, v);
    }
  }

  clearAllHistory(): void {
    for (let i = 1; i <= 10; i++) this.setHistory(i, 0);
  }

  // -----------------------------------------------------------------
  // Text runs
  // -----------------------------------------------------------------

  /** Text runs that this .riv build doesn't expose — attempted once, then skipped. */
  private readonly missingTextRuns = new Set<string>();

  /** Guards the one-time odds read-back diagnostic in setAllOdds. */
  private oddsVerified = false;

  /**
   * Set a text run on the Main artboard. Silently no-ops if the run doesn't
   * exist on this build of the .riv. A missing run is remembered after the
   * first attempt so we don't hammer the Rive runtime (which logs a console
   * error every call) on every subsequent write — e.g. the re-exported
   * carracer.riv has no `txtBalance` run, so the balance write would otherwise
   * spam the console once per frame-ish update.
   */
  setText(name: string, value: string): void {
    if (!this.instance) return;
    if (this.missingTextRuns.has(name)) return;
    try {
      this.instance.setTextRunValue(name, value);
    } catch {
      // Run not present/queryable on this artboard — cache and stop retrying.
      this.missingTextRuns.add(name);
    }
  }

  setBalanceText(value: string): void {
    this.setText(TextRuns.balance, value);
  }

  setBetLimits(minText: string, maxText: string): void {
    this.setText(TextRuns.minBet, minText);
    this.setText(TextRuns.maxBet, maxText);
  }

  setTotalBet(value: string): void {
    this.setText(TextRuns.totalBet, value);
  }

  setResultAmount(value: string): void {
    this.setText(TextRuns.resultAmount, value);
  }

  /**
   * Set the per-area sub-total text (player's own wagers). `area` is 1..8.
   * Empty string clears the slot.
   */
  setSubTotal(area: number, value: string): void {
    if (area < 1 || area > 8) return;
    this.setText(TextRuns.subTotal(area), value);
  }

  /**
   * Set the per-area all-players total (multiplayer). `area` is 1..8.
   */
  setAllTotal(area: number, value: string): void {
    if (area < 1 || area > 8) return;
    this.setText(TextRuns.allTotal(area), value);
  }

  /**
   * Set the payout-odds labels from the backend GameOdd1 list. Mapping (per
   * spec): odds[0] -> txtOdd8, odds[7] -> txtOdd1, formatted "x2".."x40".
   * e.g. GameOdd1 "2.00,2.00,2.00,2.00,10.00,15.00,30.00,40.00".
   */
  setAllOdds(odds: ReadonlyArray<number>): void {
    if (odds.length === 0) return;
    for (let i = 0; i < odds.length && i < 8; i++) {
      this.setText(TextRuns.odd(8 - i), formatOdd(odds[i]));
    }
    // One-time diagnostic: read txtOdd1 back so the console confirms whether the
    // run is reachable as a top-level Main-artboard run. If this logs a value,
    // the odds are wired; if it warns, the run needs a nested path instead.
    if (!this.oddsVerified && this.instance) {
      this.oddsVerified = true;
      try {
        const v = (
          this.instance as unknown as { getTextRunValue(n: string): string }
        ).getTextRunValue(TextRuns.odd(1));
        debugLog('[Rive] odds applied — txtOdd1 read-back =', v);
      } catch (e) {
        warn('[Rive] txtOdd1 not reachable as a top-level run (needs path?):', e);
      }
    }
  }

  clearAllSubTotals(): void {
    for (let i = 1; i <= 8; i++) this.setSubTotal(i, '');
  }

  /**
   * Reset every all-players total to "0". Used at round start and teardown so
   * the txtAllTotal row reads 0 across the board instead of the .riv's baked-in
   * default placeholder text.
   */
  clearAllAllTotals(): void {
    for (let i = 1; i <= 8; i++) this.setAllTotal(i, '0');
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level input plumbing
  // -----------------------------------------------------------------

  private setNumberAny(name: string, value: number): boolean {
    const inp = this.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' is ${inp.type}, not number`);
      return false;
    }
    debugLog(`[Rive] number '${name}' not found`);
    return false;
  }

  private setBoolAny(name: string, value: boolean): boolean {
    const inp = this.inputs.get(name);
    if (inp?.type === 'boolean') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' is ${inp.type}, not boolean`);
      return false;
    }
    debugLog(`[Rive] bool '${name}' not found`);
    return false;
  }

  private fireTriggerAny(name: string): boolean {
    const inp = this.inputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' is ${inp.type}, not trigger`);
      return false;
    }
    debugLog(`[Rive] trigger '${name}' not found`);
    return false;
  }

  // --- winlose-overlay input setters (separate inputs map) ---

  private setNumberWinlose(name: string, value: number): boolean {
    const inp = this.winloseInputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    debugLog(`[Rive] winlose number '${name}' not found`);
    return false;
  }

  private fireTriggerWinlose(name: string): boolean {
    const inp = this.winloseInputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    debugLog(`[Rive] winlose trigger '${name}' not found`);
    return false;
  }

  /** Silent at-path setter — returns true on success. */
  private trySetNumberAtPathSilent(
    inputName: string,
    value: number,
    path: string,
  ): boolean {
    if (!this.instance) return false;
    try {
      this.instance.setNumberStateAtPath(inputName, value, path);
      return true;
    } catch {
      return false;
    }
  }
}
